import { NotificationPerGroupResponse, NotificationPerGroupTemplate } from '@/types/api/response/notificationTypes';
import { NotificationChartData, NotificationDetail, UsageRateItemInterface } from '@/types/notifications';
import { interpolateColors } from '../color-generator';

export const countAllNotifications = (groups: Array<NotificationPerGroupResponse>): number => {
	return groups.reduce((acc, group) => acc + group.notificationCount, 0);
};

export const createUsageRateItems = (groups: Array<NotificationPerGroupResponse>, label: string): Array<UsageRateItemInterface> => {
	const usageRateItems: Array<UsageRateItemInterface> = [];
	const { detailItems, colors } = createDetailItems(groups, countAllNotifications(groups));
	/**
	 * Create usage rate items and chart for the groups
	 */
	usageRateItems.push({
		title: label,
		chartData: createChartDataForDetailItems(detailItems, colors),
		detailItems: addTotalForDetailItems(detailItems),
	});
	/**
	 * Create usage rate items and chart for the templates
	 */
	groups.forEach((group) => {
		const { detailItems, colors } = createDetailItems(group.templates, group.notificationCount);

		usageRateItems.push({
			title: `${group.name} - Templates`,
			chartData: createChartDataForDetailItems(detailItems, colors),
			detailItems: addTotalForDetailItems(detailItems),
		});
	});

	return usageRateItems;
};

const isTotalNotificationNonZero = (detailItems: Array<NotificationDetail>) => {
	const total = detailItems.reduce((acc, item) => acc + item.count, 0);

	return total > 0;
};

const getCountAndPercentage = (count: number, total: number) => {
	if (count === 0) {
		return [0, 0];
	}

	return [count, Math.floor((count / total) * 100)];
};

/**
 * Calculates the relative luminance of a color (RGB or hex format)
 * Returns a value between 0 (dark) and 1 (light)
 * Based on WCAG guidelines: https://www.w3.org/WAI/GL/wiki/Relative_luminance
 */
const getColorLuminance = (color: string): number => {
	let r: number, g: number, b: number;

	// Parse RGB format: 'rgb(2, 56, 88)'
	if (color.startsWith('rgb')) {
		const match = color.match(/rgb\((\d+),\s*(\d+),\s*(\d+)\)/);

		if (match) {
			r = parseInt(match[1], 10) / 255;
			g = parseInt(match[2], 10) / 255;
			b = parseInt(match[3], 10) / 255;
		} else {
			// Fallback: assume dark color if parsing fails
			return 0;
		}
	} else {
		// Parse hex format: '#023858' or '023858'
		const hex = color.replace('#', '');
		r = parseInt(hex.substring(0, 2), 16) / 255;
		g = parseInt(hex.substring(2, 4), 16) / 255;
		b = parseInt(hex.substring(4, 6), 16) / 255;
	}

	// Apply gamma correction
	const [rLinear, gLinear, bLinear] = [r, g, b].map((val) => {
		return val <= 0.03928 ? val / 12.92 : Math.pow((val + 0.055) / 1.055, 2.4);
	});

	// Calculate relative luminance
	return 0.2126 * rLinear + 0.7152 * gLinear + 0.0722 * bLinear;
};

/**
 * Determines the best text color (black or white) based on background color luminance
 */
const getContrastTextColor = (bgColor: string): 'black' | 'white' => {
	const luminance = getColorLuminance(bgColor);

	// If luminance is greater than 0.5, the color is light, use black text
	// Otherwise, use white text
	return luminance > 0.5 ? 'black' : 'white';
};

const createChartDataForDetailItems = (detailItems: Array<NotificationDetail>, colors: Array<string>) => {
	/**
	 * If there are no notifications, return an empty chart
	 */
	if (!isTotalNotificationNonZero(detailItems)) {
		return {
			labels: [],
			datasets: [{ data: [1], backgroundColor: ['#f1f1f1'] }],
		};
	}

	const chartData: NotificationChartData = {
		labels: [],
		datasets: [{ data: [], backgroundColor: colors }],
	};

	detailItems.forEach((item) => {
		chartData.labels.push(item.label);
		chartData.datasets[0].data.push(item.percentage);
	});

	return chartData;
};

const addTotalForDetailItems = (detailItems: Array<NotificationDetail>) => {
	detailItems.push({
		label: 'Total',
		count: detailItems.reduce((acc, item) => acc + item.count, 0),
		percentage: 100,
		bgColor: '#ECF4F8',
		color: 'black',
	});

	return detailItems;
};

const createDetailItems = (items: Array<NotificationPerGroupResponse | NotificationPerGroupTemplate>, totalCount: number) => {
	let colorIndex = 0;
	const colors = interpolateColors(items.length);
	const sortedByNotification = items.sort((a, b) => b.notificationCount - a.notificationCount);

	const detailItems: Array<NotificationDetail> = sortedByNotification.map((item) => {
		const [count, percentage] = getCountAndPercentage(item.notificationCount, totalCount);

		const detailItem: NotificationDetail = {
			label: item.name,
			count: count,
			percentage: percentage,
			bgColor: colors[colorIndex],
			// Determine text color based on background color luminance for optimal contrast
			color: getContrastTextColor(colors[colorIndex]),
		};

		colorIndex++;

		return detailItem;
	});

	return { detailItems, colors };
};
